import { useMemo } from 'react';
import { EligibilityType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import { Coverage } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { InsuranceCoverageResponse } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import { isEmpty } from 'lodash-es';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { IntegrationsQueries } from '@frontend/api-integrations';
import { PersonTypes } from '@frontend/api-person';
import { useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails } from '@frontend/insurance-verification';
import { DefaultInsuranceDetailsValues } from '../utils/constants';
import type {
  InsuranceDetails,
  UndefinedOrNull,
  UseGetInsuranceDetailsDentalOrNonDentalResponse,
} from '../utils/types';
import { useGetDentalInsuranceDetails } from './use-get-dental-insurance-details';
import { useGetLocationVerticalForPayerList } from './use-get-location-vertical-for-payer-list';
import { useGetNonDentalInsuranceDetails } from './use-get-non-dental-insurance-details';
import { useGetVerifiedInsuranceDetailsShallowStore } from './use-get-verified-insurance-details-store';

interface GetInsuranceDetailsData {
  isLoading: boolean;
  insuranceDetails: UndefinedOrNull<InsuranceDetails>;
  hasActiveOrVerifiedInsurance: boolean;
  isAutoVerification: boolean;
  hasErrors: boolean;
  hasCoverage: boolean;
  isInsuranceDetailsWritebackSupported: boolean;
  requestId?: string;
  personDetailsCoverage: UndefinedOrNull<Coverage>;
  forceFetch?: boolean;
}

const useGetInsuranceDetails = (
  locationId: string,
  personInfo: PersonTypes.Person,
  forceFetch = false
): GetInsuranceDetailsData => {
  const personId = personInfo?.PersonID || '';
  const { setVerifiedInsuranceDetails } = useGetVerifiedInsuranceDetailsShallowStore('setVerifiedInsuranceDetails');

  const { locationVerticalForPayerList, isDental, isWellnessVertical } = useGetLocationVerticalForPayerList(
    locationId || ''
  );
  const { locationIdWiseCustomizationFlagDetails } = useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails();

  const isEnhancedInsuranceVerificationEnabled = locationIdWiseCustomizationFlagDetails?.[locationId!] || false;

  const { isInsuranceDetailsWritebackSupported, isLoading: isLoadingWritebackSupportedDetails } =
    IntegrationsQueries.useIsWritebackSupported({
      locationId: locationId!,
      sourceId: personInfo?.SourceID || '',
    });
  //This API is responsible for getting all the details of insurance by perosn like personId, firstName, lastname
  const { data: insurancePersonDetails, isLoading: isLoadingInsuranceDetailsByPerson } =
    InsuranceDetailQueries.useGetInsuranceDetailsByPersonId({
      personId,
      locationId: locationId || '',
      options: {
        enabled: !!personId && !!locationId && isDental,
      },
    });
  const { data: payerListResponse } = InsuranceDetailQueries.useGetPayerList({
    locationId: locationId || '',
    vertical: locationVerticalForPayerList,
    eligibilityType: isEnhancedInsuranceVerificationEnabled ? EligibilityType.ENHANCED : EligibilityType.LITE,
    options: {
      enabled: !!locationId,
    },
  });

  const nonDentalData = useGetNonDentalInsuranceDetails(
    isWellnessVertical,
    personId,
    locationId,
    insurancePersonDetails,
    payerListResponse
  );
  const dentalData = useGetDentalInsuranceDetails(
    isDental,
    personId,
    locationId,
    insurancePersonDetails,
    payerListResponse,
    forceFetch
  );
  const {
    isLoading: isLoadingInsuranceDetails,
    insuranceDetails,
    thirdPartyCoverage,
    hasActiveOrVerifiedInsurance,
  } = (isDental ? dentalData : nonDentalData) as UseGetInsuranceDetailsDentalOrNonDentalResponse;
  const isLoading =
    isLoadingInsuranceDetails || isLoadingWritebackSupportedDetails || isLoadingInsuranceDetailsByPerson;

  // Setting up the verified insurance details
  const verifiedInsuranceDetails = useMemo<InsuranceDetails>(() => {
    if (!insuranceDetails) {
      setVerifiedInsuranceDetails({ ...DefaultInsuranceDetailsValues, personId: '' });
      return DefaultInsuranceDetailsValues;
    }

    setVerifiedInsuranceDetails({ ...insuranceDetails, personId });
    return insuranceDetails;
  }, [insuranceDetails, personId]);

  const isAutoVerification = !!thirdPartyCoverage?.isAutoVerification;
  const hasErrors = !isEmpty((thirdPartyCoverage as InsuranceCoverageResponse)?.errorDetails);
  const hasCoverage = !isEmpty(thirdPartyCoverage?.coverage);
  const requestId = (thirdPartyCoverage as InsuranceCoverageResponse)?.externalRequestId;
  return {
    isLoading,
    insuranceDetails: verifiedInsuranceDetails,
    hasActiveOrVerifiedInsurance,
    isAutoVerification,
    hasErrors,
    hasCoverage,
    isInsuranceDetailsWritebackSupported,
    requestId,
    personDetailsCoverage: insurancePersonDetails?.coverage,
  };
};

export default useGetInsuranceDetails;
